import React, { useState } from 'react';
import { MapPin, Trash2, Edit2, Plus, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useWines } from '@/contexts/WinesContext';

interface LocationManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LocationManagementModal: React.FC<LocationManagementModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { locations, wines, createLocation, updateLocation, deleteLocation } = useWines();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<{ id: string; name: string } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleClose = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setIsAdding(false);
    setNewName('');
    setNewDescription('');
    onClose();
  };

  const handleStartEdit = (locationId: string) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      setEditingId(locationId);
      setEditName(location.name);
      setEditDescription(location.description || '');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) {
      alert('Il nome della posizione è obbligatorio');
      return;
    }

    setLoading(true);
    try {
      await updateLocation(editingId, {
        name: editName,
        description: editDescription,
      });
      setEditingId(null);
      setEditName('');
      setEditDescription('');
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = async () => {
    if (!newName.trim()) {
      alert('Il nome della posizione è obbligatorio');
      return;
    }

    setLoading(true);
    try {
      await createLocation(newName, newDescription);
      setIsAdding(false);
      setNewName('');
      setNewDescription('');
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (locationId: string) => {
    const location = locations.find(loc => loc.id === locationId);
    if (!location) return;

    setLocationToDelete({ id: locationId, name: location.name });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!locationToDelete) return;

    setLoading(true);
    try {
      await deleteLocation(locationToDelete.id);
      setShowDeleteModal(false);
      setLocationToDelete(null);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setLocationToDelete(null);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Gestisci Posizioni</h2>
              <p className="text-sm text-gray-600 mt-1">
                {locations.length}/10 posizioni create
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Add New Location Button */}
            {!isAdding && locations.length < 10 && (
              <Button
                icon={<Plus className="w-5 h-5" />}
                onClick={() => setIsAdding(true)}
                variant="outline"
                className="w-full"
              >
                Nuova Posizione
              </Button>
            )}

            {/* Add New Location Form */}
            {isAdding && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-wine-purple rounded-lg p-4 bg-wine-purple/5"
              >
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Aggiungi Nuova Posizione</h3>
                <div className="space-y-3">
                  <Input
                    label="Nome"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="es. Cantina, Casa al mare, Frigorifero"
                    required
                  />
                  <Input
                    label="Descrizione (opzionale)"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="es. Scaffale centrale"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleAddLocation}
                      disabled={loading || !newName.trim()}
                      size="sm"
                    >
                      Salva
                    </Button>
                    <Button
                      onClick={() => {
                        setIsAdding(false);
                        setNewName('');
                        setNewDescription('');
                      }}
                      variant="outline"
                      size="sm"
                      disabled={loading}
                    >
                      Annulla
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Locations List */}
            {locations.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Nessuna Posizione
                </h3>
                <p className="text-sm text-gray-600">
                  Crea la tua prima posizione per organizzare i tuoi vini
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {locations.map((location) => (
                  <motion.div
                    key={location.id}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border border-gray-200 rounded-lg p-4 hover:border-wine-purple transition-colors"
                  >
                    {editingId === location.id ? (
                      /* Edit Mode */
                      <div className="space-y-3">
                        <Input
                          label="Nome"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          required
                        />
                        <Input
                          label="Descrizione (opzionale)"
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={handleSaveEdit}
                            disabled={loading || !editName.trim()}
                            size="sm"
                            icon={<Check className="w-4 h-4" />}
                          >
                            Salva
                          </Button>
                          <Button
                            onClick={handleCancelEdit}
                            variant="outline"
                            size="sm"
                            disabled={loading}
                          >
                            Annulla
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <MapPin className="w-5 h-5 text-wine-purple mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900">{location.name}</h3>
                            {location.description && (
                              <p className="text-sm text-gray-600 mt-1">{location.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4 flex-shrink-0">
                          <button
                            onClick={() => handleStartEdit(location.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Modifica"
                          >
                            <Edit2 className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(location.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200">
            <Button onClick={handleClose} variant="outline" className="w-full">
              Chiudi
            </Button>
          </div>
        </motion.div>
      </motion.div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && locationToDelete && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4"
          onClick={handleCancelDelete}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg max-w-md w-full p-6"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Conferma Eliminazione
            </h3>

            <p className="text-gray-700 mb-4">
              Sei sicuro di voler eliminare la posizione <strong>"{locationToDelete.name}"</strong>?
            </p>

            {(() => {
              const winesCount = wines.filter(w => w.locationId === locationToDelete.id).length;
              if (winesCount > 0) {
                return (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-amber-800">
                      <strong>Attenzione:</strong> Questa posizione è attualmente assegnata a <strong>{winesCount} {winesCount === 1 ? 'vino' : 'vini'}</strong>.
                      {' '}Eliminandola, {winesCount === 1 ? 'il vino tornerà' : 'i vini torneranno'} senza posizione assegnata.
                    </p>
                  </div>
                );
              }
              return null;
            })()}

            <div className="flex gap-3">
              <Button
                onClick={handleConfirmDelete}
                variant="danger"
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Eliminazione...' : 'Elimina'}
              </Button>
              <Button
                onClick={handleCancelDelete}
                variant="outline"
                disabled={loading}
                className="flex-1"
              >
                Annulla
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
